import React from "react";
import { ExternalLink, FileText, Paperclip, UploadCloud } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { formatINR, formatNum, resolveImg } from "@/lib/api";
import { PAY_STATUS_STYLE } from "@/pages/admin/purchases/purchaseHelpers";

// Read-only purchase detail view: line items, dual-currency payable summary, payment history, attachments.
export const PurchaseDetailDialog = ({ detail, onClose, uploadingAttachment, detailFileInputRef, onUploadAttachment }) => (
  <Dialog open={!!detail} onOpenChange={(o) => !o && onClose()}>
    <DialogContent data-testid="purchase-detail-dialog" className="max-w-2xl max-h-[85vh] overflow-y-auto">
      {detail && (
        <>
          <DialogHeader><DialogTitle className="flex items-center gap-2 flex-wrap">{detail.purchase_no} <Badge className={PAY_STATUS_STYLE[detail.payment_status]}>{detail.payment_status}</Badge></DialogTitle></DialogHeader>
          <div className="text-sm space-y-1 text-muted-foreground mb-2">
            <div>Supplier: <span className="text-foreground">{detail.supplier_name}</span></div>
            <div>Date: {detail.purchase_date}</div>
          </div>
          <Table>
            <TableHeader><TableRow><TableHead>Item</TableHead><TableHead>Type</TableHead><TableHead className="text-right">Total</TableHead></TableRow></TableHeader>
            <TableBody>{(detail.items || []).map((it) => (
              <TableRow key={it.id}><TableCell>{it.item_name}</TableCell><TableCell className="capitalize text-xs">{it.purchase_type}</TableCell><TableCell className="text-right font-mono">{formatINR(it.line_total)}</TableCell></TableRow>
            ))}</TableBody>
          </Table>
          <div className="flex justify-end gap-4 text-sm mt-2">
            <span>Subtotal: <span className="font-mono">{formatINR(detail.subtotal)}</span></span>
            <span>GST: <span className="font-mono">{formatINR(detail.gst_amount)}</span></span>
            <span className="font-medium">Total: <span className="font-mono">{formatINR(detail.total)}</span></span>
          </div>
          {(detail.gold_payable_fine_g || 0) > 0 && (
            <div className="mt-2 rounded-lg border border-border bg-[#F4EBDD] p-2.5 grid grid-cols-3 gap-2 text-center">
              <div><div className="text-[10px] text-muted-foreground uppercase">Gold Payable</div><div className="font-mono text-sm">{formatNum(detail.gold_payable_fine_g)}g</div></div>
              <div><div className="text-[10px] text-muted-foreground uppercase">Gold Paid</div><div className="font-mono text-sm text-[#1F5B2E]">{formatNum(detail.gold_paid_fine_g)}g</div></div>
              <div><div className="text-[10px] text-muted-foreground uppercase">Gold Outstanding</div><div className="font-mono text-sm text-[#7A1F1F]">{formatNum(detail.gold_outstanding_fine_g)}g</div></div>
            </div>
          )}
          {(detail.payments || []).length > 0 && (
            <div className="mt-3">
              <div className="text-sm font-medium mb-1">Payments</div>
              {detail.payments.map((pm) => (
                <div key={pm.id} className="flex items-center justify-between text-sm border-b border-border py-1">
                  <span className="text-xs capitalize">{(pm.mode || "").replace(/_/g, " ")}</span>
                  <span className="text-xs text-muted-foreground">{pm.payment_date}</span>
                  {(pm.mode || "").includes("gold") && pm.mode !== "cash_to_gold" ? (
                    <span className="font-mono text-xs text-[#1F5B2E]">{formatNum(pm.gold_fine_g)}g fine{pm.mode === "gold_to_cash" ? ` · ${formatINR(pm.cash_value)}` : ""}</span>
                  ) : (
                    <span className="font-mono text-[#1F5B2E]">{formatINR(pm.amount)}{pm.mode === "cash_to_gold" ? ` (${formatNum(pm.gold_fine_g)}g)` : ""}</span>
                  )}
                </div>
              ))}
            </div>
          )}
          <div className="mt-3">
            <div className="flex items-center justify-between mb-1">
              <div className="text-sm font-medium flex items-center gap-1.5"><Paperclip className="h-3.5 w-3.5" /> Attachments</div>
              {detail.status !== "cancelled" && (
                <>
                  <Button type="button" size="sm" variant="outline" className="gap-1" disabled={uploadingAttachment} onClick={() => detailFileInputRef.current?.click()}>
                    <UploadCloud className="h-3.5 w-3.5" /> {uploadingAttachment ? "Uploading..." : "Add Attachment"}
                  </Button>
                  <input ref={detailFileInputRef} type="file" accept="image/*,.pdf" multiple className="hidden" data-testid="purchase-detail-attachment-input"
                    onChange={(e) => { onUploadAttachment(e.target.files); e.target.value = ""; }} />
                </>
              )}
            </div>
            {(detail.attachments || []).length === 0 ? (
              <p className="text-xs text-muted-foreground">No bills or challans attached to this purchase yet.</p>
            ) : (
              <div className="space-y-1" data-testid="purchase-detail-attachment-list">
                {detail.attachments.map((a, i) => (
                  <a key={a.url || i} href={resolveImg(a.url)} target="_blank" rel="noopener noreferrer"
                    className="flex items-center justify-between gap-2 rounded-md border border-border bg-[#F7F0E6] px-2 py-1.5 text-xs text-[hsl(var(--primary))] hover:underline">
                    <span className="flex items-center gap-1.5 truncate"><FileText className="h-3.5 w-3.5 shrink-0" /> <span className="truncate text-foreground">{a.filename || `Attachment ${i + 1}`}</span></span>
                    <ExternalLink className="h-3.5 w-3.5 shrink-0" />
                  </a>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </DialogContent>
  </Dialog>
);
